'use client';

/**
 * IntentHistoryCard — explorer-surface flat history list.
 *
 * Parallel implementation. Reads `intent-store.history` and calls
 * `intent-store.loadTurn(turn)` — same data contract the legacy
 * `intent-history-section.tsx` consumes, but a fresh component
 * owned by mode3. Legacy section stays in place for the legacy
 * `intent` mode; mode3 doesn't import it.
 *
 * Click a turn → restores the cached response + conversation id from
 * the store (no new BFF call); the canvas re-paints from the
 * rehydrated state.
 *
 * @see klyde_edge/composition/cards/intent.py — content_key="intent_history"
 *
 * @component_role: Card
 * @component_type: Explorer Card
 * @domain: Explorer / Intent
 * @layer: presentation
 */

import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useExplorerLabels } from '@/lib/stores/total-matrix-store';
import { text, treeDepth, surfaceBase } from '@/lib/design/tokens.x';

export function IntentHistoryCard() {
  const history = useIntentStore((s) => s.history);
  const loadTurn = useIntentStore((s) => s.loadTurn);
  const L = useExplorerLabels('intent');

  if (history.length === 0) return null;

  return (
    <div style={{ fontSize: surfaceBase.canvas }}>
      <div className="px-1 pb-1.5">
        <h3 className={text.eyebrow}>
          {L.history_section_header ?? 'History'}
        </h3>
      </div>
      <div className="space-y-0.5">
        {history.map((turn) => (
          <button
            key={turn.id}
            type="button"
            onClick={() => loadTurn(turn)}
            className={cn(
              'w-full flex items-start gap-2 px-2 py-1.5 text-left rounded-md',
              'hover:bg-accent/50 transition-colors',
            )}
          >
            <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <p className={cn(treeDepth[0], 'truncate')}>{turn.query || '(untitled)'}</p>
              <p className={text.muted}>
                {turn.resultCount} {L.history_results_suffix ?? 'results'}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
